import { useCallback, useState } from "react";
import { useField, useFormikContext } from "formik";
import { v4 as uuidv4 } from "uuid";
import { Button, Card, ErrorMessage, Flex, Select } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { getCatalogueValues, getNextLevelToAdd } from "../../../../shared/levels/levelStack";
import {
  isNoneLevelSelection,
  NONE_LEVEL_OPTION,
  NONE_LEVEL_VALUE,
} from "../../../../shared/levels/noneLevel";
import * as styles from "./styles_vanilla.css";
import type { QueryRollupLevelOption } from "../../../../shared/levels/types";

type LevelsCardProps = {
  rollupLevelOptions: ReadonlyArray<QueryRollupLevelOption>;
  isReadOnly?: boolean;
  /** Formik path for the levels string array. Defaults to `"levels"`. */
  valuesPath?: string;
  /** When `"inline"`, renders only the level controls (no outer Card). */
  variant?: "card" | "inline";
  /** Offer "None" (no level; page-level execution) as the first option.
   * None is exclusive: selecting it collapses the stack to a single row.
   * Only meaningful for primary fields, where an empty persisted stack
   * has defined backend semantics. */
  allowNone?: boolean;
};

const LABEL_BY_INDEX: Record<number, string> = {
  0: "First level",
  1: "Second level",
  2: "Third level",
  3: "Fourth level",
  4: "Fifth level",
};

const levelLabel = (index: number) => LABEL_BY_INDEX[index] ?? `Level ${index + 1}`;

/** Grow or trim a stable-id list to a target length. Trimming pops from
 * the tail; the per-row handlers below splice at the deletion index so
 * the surviving rows keep their original identity (otherwise the DS
 * `Select` for the row after a deletion would inherit the dropped
 * row's instance state — open dropdown, focus, scroll). */
const syncIdsToLength = (prev: string[], targetLength: number): string[] => {
  if (prev.length === targetLength) return prev;
  if (prev.length < targetLength) {
    return [...prev, ...Array.from({ length: targetLength - prev.length }, () => uuidv4())];
  }
  return prev.slice(0, targetLength);
};

export const LevelsCard = ({
  rollupLevelOptions,
  isReadOnly = false,
  valuesPath = "levels",
  variant = "card",
  allowNone = false,
}: LevelsCardProps) => {
  const [{ value: levels }, meta, { setValue }] = useField<string[]>(valuesPath);
  const { submitCount } = useFormikContext();
  const catalogueValues = getCatalogueValues(rollupLevelOptions);

  // Array-level error (`min(1)`) has no input row to attach to when the
  // array is empty, so render it standalone after a submit attempt.
  const levelsError = submitCount > 0 && typeof meta.error === "string" ? meta.error : null;

  // Per-row id parallel to the Formik `levels: string[]` value. The form
  // shape is constrained by the BE (`raw.levels.levels: string[]`), so we
  // keep the ids in local state and splice them in lockstep with the form
  // array inside each handler. The during-render sync below covers
  // external length changes (initial mount with saved levels, parent
  // `enableReinitialize` between fields) without an effect-driven flash —
  // it follows the React docs' "adjust state when a prop changes" pattern
  // and only generates new uuids on the render where length actually grew.
  // Functional setState keeps the update safe under StrictMode double-invoke.
  const [rowIds, setRowIds] = useState<string[]>(() =>
    Array.from({ length: levels.length }, () => uuidv4()),
  );
  if (rowIds.length !== levels.length) {
    setRowIds((prev) => syncIdsToLength(prev, levels.length));
  }

  const handleChange = useCallback(
    (index: number, newValue: string) => {
      // None is exclusive — selecting it collapses the stack to a single
      // row (the render-time id sync trims `rowIds` to match).
      if (newValue === NONE_LEVEL_VALUE) {
        void setValue([NONE_LEVEL_VALUE]);
        return;
      }
      const next = [...levels];
      next[index] = newValue;
      void setValue(next);
    },
    [levels, setValue],
  );

  const handleRemove = useCallback(
    (index: number) => {
      if (index === 0) return;
      void setValue(levels.slice(0, index));
      setRowIds((prev) => prev.slice(0, index));
    },
    [levels, setValue],
  );

  const handleAddLevel = useCallback(() => {
    const nextLevel = getNextLevelToAdd(catalogueValues, levels);
    if (!nextLevel) return;
    void setValue([...levels, nextLevel]);
    setRowIds((prev) => [...prev, uuidv4()]);
  }, [catalogueValues, levels, setValue]);

  const isNoneSelected = allowNone && isNoneLevelSelection(levels);
  const canAddMore = !isNoneSelected && getNextLevelToAdd(catalogueValues, levels) !== null;

  // None is only offered on the first row — it cannot coexist with other
  // levels, so deeper rows (which imply a stack) never show it.
  const optionsForRow = (index: number): ReadonlyArray<QueryRollupLevelOption> =>
    allowNone && index === 0 ? [NONE_LEVEL_OPTION, ...rollupLevelOptions] : rollupLevelOptions;

  const body = (
    <Flex flexDirection="column" gap={SPACING.MEDIUM}>
      {levels.map((level, index) => (
        <Flex key={rowIds[index]} alignItems="flex-end" gap={SPACING.SMALL}>
          <Select
            label={levelLabel(index)}
            value={level}
            className={styles.formControl}
            onChange={(next: unknown) => {
              if (typeof next === "string") handleChange(index, next);
            }}
            disabled={isReadOnly}
            data-testid={`level-select-${index}`}
          >
            <Select.Section>
              {optionsForRow(index).map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  disabled={levels.includes(option.value) && option.value !== level}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
          {!isReadOnly && index > 0 && (
            <Button
              variant="muted"
              icon="trash"
              aria-label={`Remove ${levelLabel(index).toLowerCase()}`}
              onClick={() => handleRemove(index)}
              data-testid={`level-remove-${index}`}
            />
          )}
        </Flex>
      ))}

      {levelsError && <ErrorMessage>{levelsError}</ErrorMessage>}

      {!isReadOnly && canAddMore && (
        <Button
          variant="link"
          icon="plus"
          iconPosition="left"
          onClick={handleAddLevel}
          data-testid="level-add-btn"
          className={styles.levelsAddButton}
        >
          Add next level
        </Button>
      )}
    </Flex>
  );

  if (variant === "inline") return body;

  return (
    <Card isFullWidth title="Levels">
      {body}
    </Card>
  );
};
